import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import type { AssignmentProcess } from '@bliks-io/contracts';
import { Badge, cn } from '@bliks-io/ui';

interface ReviewProcessDetailsProps {
  process: AssignmentProcess;
}

/**
 * Displays process details in a card section within review modals
 * Shows process name, department, and description
 */
export function ReviewProcessDetails({ process }: ReviewProcessDetailsProps) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const [isTruncated, setIsTruncated] = useState(false);
  const descriptionRef = useRef<HTMLDivElement>(null);

  useEffect(
    function measureDescriptionTruncation() {
      if (descriptionRef.current) {
        setIsTruncated(descriptionRef.current.scrollHeight > descriptionRef.current.clientHeight);
      }
    },
    [process.description]
  );

  return (
    <div className="space-y-2 h-full flex flex-col">
      <h3 className="text-sm font-medium">{t('myTasks.process')}</h3>
      <div className="rounded-lg border bg-muted/30 p-3 space-y-2 flex-1">
        {/* Process name and department */}
        <div className="flex items-center gap-2">
          <span className="font-semibold">{process.name}</span>
          {typeof process.department === 'object' && process.department !== null && (
            <Badge variant="secondary" className="text-xs">
              {process.department.name}
            </Badge>
          )}
        </div>

        {/* Description */}
        {process.description && (
          <div>
            <div
              ref={descriptionRef}
              className={cn('text-sm text-muted-foreground', !expanded && 'line-clamp-1')}
            >
              {process.description}
            </div>
            {isTruncated && (
              <button
                type="button"
                onClick={() => setExpanded(!expanded)}
                className="text-sm text-primary hover:underline mt-1"
              >
                {expanded ? t('common.showLess') : t('common.showMore')}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
